import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Container, 
  Box, 
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import { 
  Settings as SettingsIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  Brush as BrandingIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  CreditCard as CreditCardIcon,
  Cookie as CookieIcon,
  Tune as TuneIcon
} from '@mui/icons-material';
import { usePageTitle } from '../hooks';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import ProfileSection from '../components/settings/ProfileSection';
import ManageUsersSection from '../components/settings/ManageUsersSection';
import ManageBillingSection from '../components/settings/ManageBillingSection';
import GeneralSettingsSection from '../components/settings/GeneralSettingsSection';
import CookieSettings from '../components/CookieSettings';

const SettingsPage: React.FC = () => {
  const { user, refreshUserData } = useAuth();
  const isAdmin = user?.role?.toLowerCase() === 'admin';
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get section from URL search params or default to 'Profile'
  const searchParams = new URLSearchParams(location.search);
  const sectionParam = searchParams.get('section');
  
  // Map URL parameter to section name
  const getSectionFromParam = (param: string | null): string => {
    if (!param) return 'Profile';
    
    switch (param.toLowerCase()) {
      case 'profile': return 'Profile';
      case 'general': return 'General';
      case 'users': return 'Manage Users';
      case 'accounts': return 'Manage Accounts';
      case 'billing': return 'Manage Billing';
      case 'privacy': return 'Privacy & Cookies';
      default: return 'Profile';
    }
  };
  
  const [selectedItem, setSelectedItem] = useState<string>(getSectionFromParam(sectionParam));
  
  // Set page title
  usePageTitle('Settings');

  // Refresh user data when page loads to ensure admin permissions are up-to-date
  useEffect(() => {
    // Refresh user data from the database to get current role/permissions
    refreshUserData();
    // Empty dependency array ensures this only runs once on mount
  }, []); // Remove refreshUserData dependency as it can cause loops

  const handleItemClick = (item: string) => {
    setSelectedItem(item);
    
    // Update URL without reloading the page
    const paramValue = getParamFromSection(item);
    const newParams = new URLSearchParams(location.search);
    newParams.set('section', paramValue);
    navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
  };
  
  // Map section name to URL parameter
  const getParamFromSection = (section: string): string => {
    switch (section) {
      case 'Profile': return 'profile';
      case 'General': return 'general';
      case 'Manage Users': return 'users';
      case 'Manage Accounts': return 'accounts';
      case 'Manage Billing': return 'billing';
      case 'Privacy & Cookies': return 'privacy';
      default: return 'profile';
    }
  };

  // Render content based on selected item
  const renderContent = () => {
    switch (selectedItem) {
      case 'Profile':
        return <ProfileSection />;
      case 'General':
        return <GeneralSettingsSection />;
      case 'Manage Users':
        return <ManageUsersSection />;
      case 'Manage Accounts':
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Social Accounts moved
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your Social Accounts at the new page under the main header tab: Social Accounts.
            </Typography>
          </Box>
        );
      case 'Manage Billing':
        return <ManageBillingSection />;
      case 'Privacy & Cookies':
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Privacy & Cookies
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Manage your cookie preferences and privacy settings.
            </Typography>
            
            <CookieSettings showTitle={false} />
            
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Your Data
              </Typography>
              <Typography variant="body2" paragraph>
                You can request a copy of your data or delete your account at any time.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                To request these actions, please contact us at privacy@wavescoop.com.
              </Typography>
            </Box>
          </Box>
        );
      default:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {selectedItem}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This feature is coming soon.
            </Typography>
          </Box>
        );
    }
  };

  return (
    <>
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Settings
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Manage your account and organization preferences
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Sidebar */}
        <Grid item xs={12} md={3}>
          <Paper>
            <List component="nav">
              <ListItem>
                <ListItemIcon>
                  <SettingsIcon />
                </ListItemIcon>
                <ListItemText primary="Settings" primaryTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }} />
              </ListItem>
              <Divider />
              
              {/* User Settings */}
              <ListItem
                button
                selected={selectedItem === 'Profile'}
                onClick={() => handleItemClick('Profile')}
              >
                <ListItemIcon>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText primary="Profile" />
              </ListItem>
              
              <ListItem
                button
                selected={selectedItem === 'General'}
                onClick={() => handleItemClick('General')}
              >
                <ListItemIcon>
                  <TuneIcon />
                </ListItemIcon>
                <ListItemText primary="General" />
              </ListItem>

              <ListItem
                button
                selected={selectedItem === 'Privacy & Cookies'}
                onClick={() => handleItemClick('Privacy & Cookies')}
              >
                <ListItemIcon>
                  <CookieIcon />
                </ListItemIcon>
                <ListItemText primary="Privacy & Cookies" />
              </ListItem>
              
              {/* Admin Section */}
              {isAdmin && (
                <>
                  <Divider sx={{ mt: 2 }} />
                  <ListItem>
                    <ListItemIcon>
                      <AdminIcon />
                    </ListItemIcon>
                    <ListItemText primary="Admin" primaryTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }} />
                  </ListItem>
                  <Divider />
                
                  <ListItem
                    button
                    selected={selectedItem === 'Manage Users'}
                    onClick={() => handleItemClick('Manage Users')}
                  >
                    <ListItemIcon>
                      <PeopleIcon />
                    </ListItemIcon>
                    <ListItemText primary="Users" />
                  </ListItem>

                  {/* Social Accounts moved to main nav; leaving a note-only section above */}
                  
                  <ListItem
                    button
                    selected={selectedItem === 'Manage Billing'}
                    onClick={() => handleItemClick('Manage Billing')}
                  >
                    <ListItemIcon>
                      <CreditCardIcon />
                    </ListItemIcon>
                    <ListItemText primary="Subscription" />
                  </ListItem>
                </>
              )}
            </List>
          </Paper>
        </Grid>

        {/* Content area */}
        <Grid item xs={12} md={9}>
          <Paper sx={{ minHeight: '400px' }}>
            {renderContent()}
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};

export default SettingsPage; 
